import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { toast } from 'react-toastify';
import Modal from '../components/Layout/Modal';
import ChangePasswordModal from '../components/Auth/ChangePasswordModal';
import DeleteAccountModal from '../components/Auth/DeleteAccountModal';
import SubscriptionModal from '../components/Layout/SubscriptionModal';
import { calculateOverallCurrentGPA, gpaToLetterGrade } from '../utils/gpa';
import { API_ENDPOINTS, API_BASE_URL } from '../api/config';
import PublicFooter from '../components/Layout/PublicFooter';

const Container = styled.div`
  height: 100%;
  display: flex;
  background: ${props => props.theme.background};
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0.75rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const AccountHeader = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme.background};

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const AccountTitle = styled.h1`
  color: ${props => props.theme.text};
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0;
  }
`;

const Section = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: ${props => props.compact ? '0.25rem' : '1.25rem'};
  margin-bottom: ${props => props.compact ? '0.25rem' : '1.5rem'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  @media (max-width: 768px) {
    padding: ${props => props.compact ? '0.125rem' : '1rem'};
  }

  @media (max-width: 480px) {
    padding: ${props => props.compact ? '0.125rem' : '0.75rem'};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.compact ? '0.25rem' : '1rem'};
  padding-bottom: ${props => props.compact ? '0.25rem' : '0.75rem'};
  border-bottom: 1px solid ${props => props.theme.border};
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: ${props => props.compact ? '0.875rem' : '1.125rem'};
  font-weight: 600;
  margin: 0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.compact ? '0.25rem' : '1rem'};
`;

const InfoItem = styled.div`
  padding: ${props => props.compact ? '0.25rem' : '1rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.cardBackgroundHover};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const InfoLabel = styled.div`
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 500;
`;

const HelpIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 0.5rem;
  cursor: pointer;
  background-color: ${props => props.theme.textSecondary};
  color: ${props => props.theme.backgroundSecondary};
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background-color: ${props => props.theme.text};
    color: #2c2f33;
    transform: scale(1.1);
  }
`;

const ActionSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.compact ? '0.25rem' : '0.5rem'};
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? props.theme.buttonDanger : props.theme.buttonPrimary};
  color: ${props => props.theme.text};
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  margin-right: 1rem;
  margin-bottom: 0.5rem;

  &:hover {
    background: ${props => props.danger ? props.theme.buttonDangerHover : props.theme.buttonPrimaryHover};
  }

  &:disabled {
    background: ${props => props.theme.buttonSecondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  margin-top: 1.5rem;
  margin-bottom: 1rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  input:checked + span {
    background-color: ${props => props.theme.buttonPrimary};
  }

  input:checked + span:before {
    transform: translateX(26px);
  }

  input:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.theme.border};
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: ${props => props.theme.text};
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const ToggleLabel = styled.div`
  color: ${props => props.theme.text};
  font-weight: 500;
  flex: 1;
`;

const ToggleStatus = styled.div`
  color: ${props => props.enabled ? props.theme.statusCompleted : props.theme.statusOverdue};
  font-weight: 600;
  font-size: 0.875rem;
`;

const SyncButton = styled.button`
  background: ${props => props.theme.buttonPrimary};
  color: ${props => props.theme.text};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const AccountGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const WarningBox = styled.div`
  background: #5c2e2e;
  border: 1px solid ${props => props.theme.buttonDanger};
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InvoicesSection = styled.div`
  margin-top: 2rem;
`;

const InvoiceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.cardBackgroundHover};
    border-color: ${props => props.theme.buttonPrimary};
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const InvoiceInfo = styled.div`
  flex: 1;
`;

const InvoiceTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 0.25rem;
`;

const InvoiceDetails = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.875rem;
`;

const InvoiceAmount = styled.div`
  font-weight: 600;
  color: ${props => props.theme.statusCompleted};
  font-size: 1.1rem;
`;

const InvoiceStatus = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
  background: ${props => {
    switch (props.status) {
      case 'paid': return '#2d5a2d';
      case 'open': return '#5a4a2d';
      case 'void': return '#5a2d2d';
      case 'draft': return '#4a4a4a';
      default: return '#4a4a4a';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'paid': return '#57f287';
      case 'open': return '#fbbf24';
      case 'void': return '#ef4444';
      case 'draft': return 'var(--color-text-muted)';
      default: return 'var(--color-text-muted)';
    }
  }};
`;

const ShowMoreButton = styled.button`
  background: ${props => props.theme.buttonPrimary};
  color: ${props => props.theme.text};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: 1rem;

  &:hover {
    background: ${props => props.theme.buttonPrimaryHover};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PageButton = styled.button`
  background: ${props => props.active ? props.theme.buttonPrimary : props.theme.border};
  color: ${props => props.theme.text};
  border: none;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.active ? props.theme.buttonPrimaryHover : props.theme.cardBackgroundHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NoInvoicesMessage = styled.div`
  text-align: center;
  color: ${props => props.theme.textSecondary};
  padding: 2rem;
  font-style: italic;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.textSecondary};
`;

const InvoiceLink = styled.a`
  color: ${props => props.theme.buttonPrimary};
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PaginationText = styled.span`
  color: ${props => props.theme.text};
  margin: 0 0.5rem;
`;

const ModalContent = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.6;
  
  p {
    margin-bottom: 1rem;
  }
  
  h4 {
    color: ${props => props.theme.buttonPrimary};
    margin-bottom: 0.5rem;
  }
  
  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }
  
  .muted {
    font-style: italic;
    color: ${props => props.theme.textSecondary};
  }
`;

const Account = () => {
  const { user, loading, refreshUser } = useAuth();
  const { assignments, courses, settings } = useData();
  
  // State declarations
  const [accountStats, setAccountStats] = useState({
    averageGrade: 'N/A',
    totalAssignments: 0,
    totalCourses: 0,
    avgAssignmentsPerCourse: 0
  });
  const [showAverageGradeHelp, setShowAverageGradeHelp] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  
  // Subscription management state
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [isProcessingRenewal, setIsProcessingRenewal] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [planPrice, setPlanPrice] = useState(user?.planPrice || null);
  const [subscriptionDataLoaded, setSubscriptionDataLoaded] = useState(false);
  
  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const invoicesPerPage = 10;
  
  // Helper function to get subscription plan details
  const getSubscriptionDetails = useCallback(() => {
    const subscription = user?.subscription || 'free';
    const aiTier = user?.aiTier;
    const subscriptionEndDate = user?.subscriptionEndDate;
    const subscriptionStatus = user?.subscriptionStatus;
    const userPlanPrice = planPrice || user?.planPrice || null;
    
    // Check if subscription has expired (expires at noon on expiration day)
    const isExpired = subscriptionEndDate ? (() => {
      const endDate = new Date(subscriptionEndDate);
      const now = new Date();
      
      // Set expiration to noon on the expiration day
      endDate.setHours(12, 0, 0, 0);
      
      return now >= endDate;
    })() : false;
    
    // Check if subscription is cancelled, failed, or inactive
    const isCancelled = subscriptionStatus === 'canceled' || 
                       subscriptionStatus === 'past_due' || 
                       subscriptionStatus === 'unpaid' ||
                       subscriptionStatus === 'incomplete_expired';

    // Check if subscription is awaiting payment authentication (3D Secure)
    const isPending = subscriptionStatus === 'incomplete';

    if (isPending) {
      return {
        status: 'Pending Payment',
        planName: 'Action Required',
        price: 'Payment incomplete',
        expired: false,
        cancelled: false,
        willCancelAtPeriodEnd: false
      };
    }
    
    // Check if subscription will cancel at period end
    const willCancelAtPeriodEnd = subscriptionStatus === 'active' && user?.cancelAtPeriodEnd;
    
    // If expired or cancelled, treat as free
    if (isExpired || isCancelled) {
      const statusReason = isCancelled ? 'Cancelled' : 'Expired';
      return {
        status: statusReason,
        planName: `Basic Plan (${statusReason})`,
        price: 'Free',
        expired: isExpired,
        cancelled: isCancelled,
        willCancelAtPeriodEnd: false
      };
    }
    
    // Check for AI plans first
    if (aiTier && !isCancelled) {
      switch (aiTier) {
        case 'ai_light':
          return {
            status: 'AI',
            planName: 'AI Light Plan',
            price: '$7.99/month',
            willCancelAtPeriodEnd
          };
        case 'ai_moderate':
          return {
            status: 'AI',
            planName: 'AI Moderate Plan',
            price: '$9.99/month',
            willCancelAtPeriodEnd
          };
        case 'ai_pro':
          return {
            status: 'AI',
            planName: 'AI Pro Plan',
            price: '$12.99/month',
            willCancelAtPeriodEnd
          };
        case 'ai_max':
          return {
            status: 'AI',
            planName: 'AI Max Plan',
            price: '$16.99/month',
            willCancelAtPeriodEnd
          };
        default:
          break;
      }
    }
    
    // Check if this is admin-added premium time
    const isAdminAddedTime = subscriptionEndDate && !user?.stripeSubscriptionId && subscription !== 'lifetime';
    
    if (isAdminAddedTime && !isCancelled) {
      return {
        status: 'Premium',
        planName: 'Admin Added Time',
        price: 'Free Premium',
        willCancelAtPeriodEnd: false
      };
    }
    
    switch (subscription) {
      case 'free':
        return {
          status: 'Free',
          planName: 'Basic Plan',
          price: 'Free'
        };
      case 'monthly':
        return {
          status: 'Premium',
          planName: 'Premium Monthly Plan',
          price: userPlanPrice || '$4.99/month',
          willCancelAtPeriodEnd
        };
      case 'quarterly':
        return {
          status: 'Premium',
          planName: 'Premium Quarterly Plan',
          price: userPlanPrice || '$4.49/month (10% off)',
          willCancelAtPeriodEnd
        };
      case 'yearly':
        return {
          status: 'Premium',
          planName: 'Premium Yearly Plan',
          price: userPlanPrice || '$3.49/month (30% off)',
          willCancelAtPeriodEnd
        };
      case 'lifetime':
        return {
          status: 'Premium',
          planName: 'Lifetime Plan',
          price: 'One-time payment',
          willCancelAtPeriodEnd: false
        };
      default:
        return {
          status: 'Free',
          planName: 'Basic Plan',
          price: 'Free',
          willCancelAtPeriodEnd: false
        };
    }
  }, [user, planPrice]);
  
  const subscriptionDetails = getSubscriptionDetails();
  
  // Get next billing date from API-loaded data (fresh from Stripe) or fallback to invoice calculation
  const getNextBillingDate = () => {
    // First priority: Use the subscriptionEndDate from API (fresh from Stripe)
    if (subscriptionEndDate) {
      return subscriptionEndDate;
    }

    // Second priority: Use user object date if available
    if (user?.subscriptionEndDate && user?.subscription !== 'lifetime') {
      return new Date(user.subscriptionEndDate);
    }

    // Fallback: Calculate from first invoice date + plan duration
    if (invoices.length > 0) {
      const firstInvoice = invoices[0];
      const purchaseDate = new Date(firstInvoice.created * 1000); // Convert from Unix timestamp

      let daysToAdd = 0;

      // Determine days to add based on plan type
      if (user?.aiTier && user.aiTier !== 'free') {
        // All AI plans are 30 days
        daysToAdd = 30;
      } else {
        // Regular subscription plans
        switch (user?.subscription) {
          case 'monthly':
            daysToAdd = 30;
            break;
          case 'quarterly':
            daysToAdd = 90;
            break;
          case 'yearly':
            daysToAdd = 365;
            break;
          default:
            daysToAdd = 0;
        }
      }

      if (daysToAdd > 0) {
        const endDate = new Date(purchaseDate);
        endDate.setDate(endDate.getDate() + daysToAdd);
        return endDate;
      }
    }

    return null;
  };
  
  const nextBillingDate = getNextBillingDate();
  
  // Check if within 48 hours of billing
  const isWithin48Hours = () => {
    if (!nextBillingDate) return false;
    const now = new Date();
    const timeDiff = nextBillingDate.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);
    return hoursDiff <= 48 && hoursDiff > 0;
  };
  
  const within48Hours = isWithin48Hours();

  useEffect(() => {
    if (!courses || !assignments || !user) return;

    // Calculate current GPA using all courses and assignments (account-wide)
    const currentGPA = calculateOverallCurrentGPA(courses, assignments, settings);
    const currentLetterGrade = gpaToLetterGrade(currentGPA, settings);
    const displayGPA = currentGPA > 0 ? `${currentGPA.toFixed(2)} (${currentLetterGrade})` : 'N/A';

    // Overall stats across all courses and assignments
    const totalCoursesAllTime = courses?.length || 0;
    const totalAssignmentsAllTime = assignments?.length || 0;
    const avgAssignmentsPerCourse = totalCoursesAllTime > 0 ? Math.round(totalAssignmentsAllTime / totalCoursesAllTime) : 0;

    setAccountStats({
      averageGrade: displayGPA,
      totalAssignments: totalAssignmentsAllTime,
      totalCourses: totalCoursesAllTime,
      avgAssignmentsPerCourse
    });
  }, [courses, assignments, user, settings]);

  // Load subscription data from API
  useEffect(() => {
    if (!user) return;
    
    setSubscriptionDataLoaded(false);
    
    const loadSubscriptionData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/subscription/status`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          
          // Set auto-renewal status
          if (data.cancelAtPeriodEnd !== undefined) {
            setAutoRenewal(!data.cancelAtPeriodEnd);
          }
          
          // Set subscription end date from API
          if (data.subscriptionEndDate) {
            setSubscriptionEndDate(new Date(data.subscriptionEndDate));
          }
          if (data.planPrice) {
            setPlanPrice(data.planPrice);
          }
          
          setSubscriptionDataLoaded(true);
        }
      } catch (error) {
        console.error('Error loading subscription data:', error);
        setSubscriptionDataLoaded(true);
      }
    };

    if (user?.planPrice) {
      setPlanPrice(user.planPrice);
    }

    loadSubscriptionData();
  }, [user]);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    // In development with mock data, return empty invoices array
    if (process.env.NODE_ENV === 'development' && user?.stripeSubscriptionId?.startsWith('mock-')) {
      setInvoices([]);
      setInvoicesLoading(false);
      return;
    }

    try {
      setInvoicesLoading(true);
      const response = await fetch(API_ENDPOINTS.INVOICES, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      } else {
        console.error('Failed to fetch invoices');
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchInvoices();
    }
  }, [user, fetchInvoices]);

  // Pagination functions
  const getDisplayedInvoices = () => {
    if (showAllInvoices) {
      // Show first 7 invoices when expanded
      return invoices.slice(0, 7);
    } else {
      // Show first 2 invoices by default
      return invoices.slice(0, 2);
    }
  };

  const getPaginatedInvoices = () => {
    if (!showAllInvoices) return [];
    
    const startIndex = (currentPage - 1) * invoicesPerPage + 7; // Skip first 7
    const endIndex = startIndex + invoicesPerPage;
    return invoices.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    if (!showAllInvoices) return 1;
    const remainingInvoices = Math.max(0, invoices.length - 7);
    return Math.ceil(remainingInvoices / invoicesPerPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleShowMore = () => {
    setShowAllInvoices(true);
    setCurrentPage(1);
  };

  const handleShowLess = () => {
    setShowAllInvoices(false);
    setCurrentPage(1);
  };

  const handlePasswordChange = () => {
    setIsChangePasswordModalOpen(true);
  };

  const handlePasswordChangeSuccess = (message) => {
    setIsChangePasswordModalOpen(false);
    toast.success(message);
  };

  const handlePasswordChangeCancel = () => {
    setIsChangePasswordModalOpen(false);
  };

  const handleDeleteAccount = () => {
    setIsDeleteAccountModalOpen(true);
  };

  const handleDeleteAccountSuccess = (message) => {
    setIsDeleteAccountModalOpen(false);
    toast.success(message);
    // The user will be logged out by the backend
    // Navigate to login or handle logout
  };

  const handleDeleteAccountCancel = () => {
    setIsDeleteAccountModalOpen(false);
  };
  
  const handleAutoRenewalToggle = async () => {
    if (within48Hours) return;
    
    // In development with mock data, just toggle locally
    if (process.env.NODE_ENV === 'development' && user?.stripeSubscriptionId?.startsWith('mock-')) {
      setAutoRenewal(!autoRenewal);
      toast.success(`Auto-renewal ${!autoRenewal ? 'enabled' : 'disabled'} successfully (mock)`);
      return;
    }
    
    setIsProcessingRenewal(true);
    
    try {
      const endpoint = autoRenewal ? API_ENDPOINTS.CANCEL_SUBSCRIPTION : API_ENDPOINTS.REACTIVATE_SUBSCRIPTION;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        const newAutoRenewal = !autoRenewal;
        setAutoRenewal(newAutoRenewal);
        toast.success(data.message || `Auto-renewal ${newAutoRenewal ? 'enabled' : 'disabled'} successfully`);
        
        // Refresh user data to get updated cancelAtPeriodEnd
        try {
          await refreshUser();
        } catch (refreshError) {
          console.error('Failed to refresh user data after toggle:', refreshError);
        }
      } else {
        toast.error(data.error || 'Failed to update auto-renewal settings. Please try again.');
      }
    } catch (error) {
      console.error('Error toggling auto-renewal:', error);
      toast.error('Failed to update auto-renewal settings. Please try again.');
    } finally {
      setIsProcessingRenewal(false);
    }
  };

  const handleSyncWithStripe = async () => {
    if (isProcessingRenewal) return;
    
    setIsProcessingRenewal(true);
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh user data (this triggers a React re-render automatically)
        await refreshUser();
        toast.success('Subscription data synced with Stripe successfully!');
      } else {
        toast.error(data.error || 'Failed to sync with Stripe');
      }
    } catch (error) {
      console.error('Error syncing with Stripe:', error);
      toast.error('Failed to sync with Stripe. Please try again.');
    } finally {
      setIsProcessingRenewal(false);
    }
  };

  if (loading || !user) {
    return (
      <Container>
        <AccountHeader>
          <AccountTitle>Loading Account...</AccountTitle>
        </AccountHeader>
      </Container>
    );
  }

  return (
    <Container>
      <MainContent>
        <AccountHeader>
          <HeaderTop>
            <AccountTitle>My Account</AccountTitle>
          </HeaderTop>
        </AccountHeader>

        {/* Main Account Overview */}
        <AccountGrid>
        {/* Left Column - Primary Info */}
        <div>
          <Section>
            <SectionHeader>
              <SectionTitle>Profile Information</SectionTitle>
            </SectionHeader>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Name</InfoLabel>
                <InfoValue>{user.name}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{user.email}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Member Since</InfoLabel>
                <InfoValue>{new Date(user.createdAt || Date.now()).toLocaleDateString()}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Last Login</InfoLabel>
                <InfoValue>{new Date().toLocaleDateString()}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

<Section>
            <SectionHeader>
              <SectionTitle>Subscription Details</SectionTitle>
            </SectionHeader>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Account Status</InfoLabel>
                <InfoValue style={{ color: subscriptionDetails.status === 'Active' || (subscriptionDetails.status === 'Premium' && subscriptionDetails.willCancelAtPeriodEnd) ? '#57f287' : subscriptionDetails.status === 'Free' ? '#ed4245' : subscriptionDetails.status === 'Expired' ? '#ed4245' : '#faa61a' }}>
                  {subscriptionDetails.willCancelAtPeriodEnd ? 'Active (Cancelling)' : subscriptionDetails.status}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Plan</InfoLabel>
                <InfoValue>{subscriptionDetails.planName}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Price</InfoLabel>
                <InfoValue>{subscriptionDetails.price}</InfoValue>
              </InfoItem>
              {(user?.subscription && user?.subscription !== 'free' && user?.subscription !== 'lifetime') && (
                <InfoItem>
                  <InfoLabel>
                    {subscriptionDetails.expired
                      ? 'Expired on'
                      : (subscriptionDetails.willCancelAtPeriodEnd
                        ? 'Cancels on'
                        : (user?.subscriptionEndDate && !user?.stripeSubscriptionId && user?.subscription !== 'lifetime'
                          ? 'Expires on'
                          : (!autoRenewal && user?.stripeSubscriptionId
                            ? 'Sub Expires On'
                            : 'Next Billing Date')))}
                  </InfoLabel>
                  <InfoValue>
                    {!subscriptionDataLoaded ? 'Loading...' : 
                     nextBillingDate ? nextBillingDate.toLocaleDateString() : 'Not available'}
                  </InfoValue>
                </InfoItem>
              )}
            </InfoGrid>

            {(user?.subscription && user?.subscription !== 'free' && user?.subscription !== 'lifetime') && (
              <>
                {!subscriptionDetails.cancelled && !subscriptionDetails.expired && (
                  <>
                    {within48Hours && (
                      <WarningBox>
                        ⚠️ Auto-renewal settings cannot be changed within 48 hours of billing date.
                      </WarningBox>
                    )}

                    <ToggleContainer>
                      <ToggleLabel>Automatic Subscription Renewal</ToggleLabel>
                      <ToggleStatus enabled={autoRenewal}>
                        {!subscriptionDataLoaded ? 'Loading...' : (autoRenewal ? 'ON' : 'OFF')}
                      </ToggleStatus>
                      <ToggleSwitch>
                        <input
                          type="checkbox"
                          checked={autoRenewal}
                          onChange={handleAutoRenewalToggle}
                          disabled={within48Hours || isProcessingRenewal || !subscriptionDataLoaded}
                        />
                        <Slider />
                      </ToggleSwitch>
                    </ToggleContainer>
                  </>
                )}

                <SyncButton
                  onClick={handleSyncWithStripe}
                  disabled={isProcessingRenewal || !subscriptionDataLoaded}
                >
                  {isProcessingRenewal ? 'Syncing...' : '🔄 Sync with Stripe'}
                </SyncButton>
              </>
            )}
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>Account Actions</SectionTitle>
            </SectionHeader>
            <ActionSection>
              <ActionButton onClick={() => setIsSubscriptionModalOpen(true)}>
                Upgrade or Change Plan
              </ActionButton>
              <ActionButton onClick={handlePasswordChange}>
                Change Password
              </ActionButton>
              <ActionButton danger onClick={handleDeleteAccount}>
                Delete Account
              </ActionButton>
            </ActionSection>
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>Invoices</SectionTitle>
            </SectionHeader>
            <InvoicesSection>
              {invoicesLoading ? (
                <LoadingText>Loading invoices...</LoadingText>
              ) : invoices.length > 0 ? (
                <>
                  {showAllInvoices ? getPaginatedInvoices().map(invoice => (
                    <InvoiceItem key={invoice.id}>
                      <InvoiceInfo>
                        <InvoiceTitle>
                          Invoice #{invoice.number}
                          <InvoiceStatus status={invoice.status}>{invoice.status}</InvoiceStatus>
                        </InvoiceTitle>
                        <InvoiceDetails>
                          {new Date(invoice.created * 1000).toLocaleDateString()} • {invoice.lines?.data[0]?.description || 'Subscription'}
                        </InvoiceDetails>
                      </InvoiceInfo>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <InvoiceAmount>
                          {(invoice.amount_paid / 100).toLocaleString('en-US', { style: 'currency', currency: invoice.currency })}
                        </InvoiceAmount>
                        {invoice.hosted_invoice_url && (
                          <InvoiceLink 
                            href={invoice.hosted_invoice_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            View
                          </InvoiceLink>
                        )}
                      </div>
                    </InvoiceItem>
                  )) : getDisplayedInvoices().map(invoice => (
                    <InvoiceItem key={invoice.id}>
                      <InvoiceInfo>
                        <InvoiceTitle>
                          Invoice #{invoice.number}
                          <InvoiceStatus status={invoice.status}>{invoice.status}</InvoiceStatus>
                        </InvoiceTitle>
                        <InvoiceDetails>
                          {new Date(invoice.created * 1000).toLocaleDateString()} • {invoice.lines?.data[0]?.description || 'Subscription'}
                        </InvoiceDetails>
                      </InvoiceInfo>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <InvoiceAmount>
                          {(invoice.amount_paid / 100).toLocaleString('en-US', { style: 'currency', currency: invoice.currency })}
                        </InvoiceAmount>
                        {invoice.hosted_invoice_url && (
                          <InvoiceLink 
                            href={invoice.hosted_invoice_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            View
                          </InvoiceLink>
                        )}
                      </div>
                    </InvoiceItem>
                  ))}

                  {!showAllInvoices && invoices.length > 2 && (
                    <div style={{ textAlign: 'center' }}>
                      <ShowMoreButton onClick={handleShowMore}>
                        Show More ({invoices.length - 2} more)
                      </ShowMoreButton>
                    </div>
                  )}

                  {showAllInvoices && (
                    <>
                      <PaginationContainer>
                        <PageButton 
                          onClick={() => handlePageChange(currentPage - 1)} 
                          disabled={currentPage === 1}
                        >
                          Previous
                        </PageButton>
                        <PaginationText>
                          Page {currentPage} of {getTotalPages()}
                        </PaginationText>
                        <PageButton 
                          onClick={() => handlePageChange(currentPage + 1)} 
                          disabled={currentPage === getTotalPages()}
                        >
                          Next
                        </PageButton>
                      </PaginationContainer>
                      <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                        <ShowMoreButton onClick={handleShowLess}>
                          Show Less
                        </ShowMoreButton>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <NoInvoicesMessage>
                  No invoices found.
                </NoInvoicesMessage>
              )}
            </InvoicesSection>
          </Section>
        </div>

        {/* Right Column - Stats & Actions */}
        <div>
          <Section>
            <SectionHeader>
              <SectionTitle>Account Statistics</SectionTitle>
            </SectionHeader>
            <div style={{ padding: '1rem' }}>
              <InfoItem style={{ marginBottom: '0.75rem' }}>
                <InfoLabel>
                  Average Grade
                  <HelpIcon onClick={() => setShowAverageGradeHelp(true)} title="Click for help">?</HelpIcon>
                </InfoLabel>
                <InfoValue>{accountStats.averageGrade} Overall GPA</InfoValue>
              </InfoItem>
              <InfoItem style={{ marginBottom: '0.75rem' }}>
                <InfoLabel>Total Assignments</InfoLabel>
                <InfoValue>{accountStats.totalAssignments}</InfoValue>
              </InfoItem>
              <InfoItem style={{ marginBottom: '0.75rem' }}>
                <InfoLabel>Total Courses</InfoLabel>
                <InfoValue>{accountStats.totalCourses}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Avg Assignments/Course</InfoLabel>
                <InfoValue>{accountStats.avgAssignmentsPerCourse}</InfoValue>
              </InfoItem>
            </div>
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>Security</SectionTitle>
            </SectionHeader>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Two-Factor Authentication</InfoLabel>
                <InfoValue style={{ color: user.twoFactorEnabled ? '#57f287' : '#ed4245' }}>
                  {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Account ID</InfoLabel>
                <InfoValue style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{user._id || user.id}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>
        </div>
      </AccountGrid>

      <Modal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        title="Change Password"
      >
        <ChangePasswordModal
          onSuccess={handlePasswordChangeSuccess}
          onCancel={handlePasswordChangeCancel}
        />
      </Modal>

      <Modal
        isOpen={isDeleteAccountModalOpen}
        onClose={() => setIsDeleteAccountModalOpen(false)}
        title="Delete Account"
      >
        <DeleteAccountModal
          onSuccess={handleDeleteAccountSuccess}
          onCancel={handleDeleteAccountCancel}
        />
      </Modal>

      <Modal
        isOpen={showAverageGradeHelp}
        onClose={() => setShowAverageGradeHelp(false)}
        title="Overall GPA Explanation"
      >
        <ModalContent>
          <p>
            Your Average Grade shows your overall GPA based on <strong>COMPLETED assignments</strong> in your courses that have been graded.
          </p>
          
          <h4>How it's calculated:</h4>
          <ul>
            <li><strong>For courses with final grades:</strong> Uses the course's final letter grade</li>
            <li><strong>For in-progress courses:</strong> Considers only COMPLETED assignments that have grades</li>
            <li><strong>Assignment status:</strong> Only assignments marked as "completed" are included</li>
            <li><strong>Graded assignments:</strong> Must have an actual grade (A=4.0, B=3.0, etc.)</li>
            <li><strong>Assignment weights:</strong> Each assignment contributes based on its importance (weight)</li>
            <li><strong>Course credits:</strong> Courses with more credits have greater impact on your overall GPA</li>
          </ul>

          <h4>What gets included:</h4>
          <ul>
            <li>✅ Only COMPLETED assignments with grades</li>
            <li>✅ Final course grades for courses you've completed</li>
            <li>✅ Assignment weights (higher weight = more GPA impact)</li>
            <li>✅ Course credit hours in the weighted average</li>
          </ul>

          <h4>What gets excluded:</h4>
          <ul>
            <li>❌ Pending/in-progress assignments (even if graded)</li>
            <li>❌ Ungraded assignments (even if completed)</li>
            <li>❌ Assignments not marked as "completed"</li>
          </ul>

          <p className="muted">
            This represents your true overall GPA based only on work you've actually completed and received grades for. Assignments must be both completed AND graded to be included in the calculation.
          </p>
        </ModalContent>
      </Modal>

      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
      />

        <PublicFooter />
      </MainContent>
    </Container>
  );
};

export default Account;
